import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type OCRScopingJobMainPanelViewModel } from '@/features/jobs/scoping/OCRScopingJobMainPanelViewModel'
import { Step } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/Step'
import { CompactWorkspaceSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactWorkspaceSelectorContainer'
import { CompactLegendSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactLegendSelectorContainer'
import { CompactOwnerSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactOwnerSelectorContainer'
import { CompactTeamSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactTeamSelectorContainer'
import { CompactRequestProviderSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactRequestProviderSelectorContainer'
import { CompactSupportingClientSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactSupportingClientSelectorContainer'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import {
  OCRScopeJobDetailBodyBasicInformation,
  OCRScopeJobDetailBodyBasicInformationDocument,
} from '@/features/jobs/scoping/components/OCRScopeJobDetailBodyBasicInformation'

export interface OCRScopeJobDetailBodyBasicInformationContainerProps {
  vm: OCRScopingJobMainPanelViewModel
}

export const OCRScopeJobDetailBodyBasicInformationContainer = observer(
  function OCRScopeJobDetailBodyBasicInformationContainer({
    vm,
  }: OCRScopeJobDetailBodyBasicInformationContainerProps) {
    const testId = 'ocr-scope-job-detail-basic-information'
    const templateSelectProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static([vm.legendTemplate!], (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.legendTemplate],
      ),
    })

    const workspaceSelector = vm.editor.workspaceSelector
    const workspaceSelectorStep = workspaceSelector.step

    const legendSelector = vm.editor.legendSelector
    const legendSelectorStep = legendSelector.step

    const ownerSelector = vm.editor.ownerSelector
    const ownerSelectorStep = ownerSelector.step

    const teamSelector = vm.editor.teamSelector
    const teamSelectorStep = teamSelector.step

    const supportingClientSelector = vm.editor.supportingClientSelector
    const supportingClientSelectorStep = supportingClientSelector.step

    const requestProviderSelector = vm.editor.requestProviderSelector
    const requestProviderSelectorStep = requestProviderSelector.step

    return (
      <OCRScopeJobDetailBodyBasicInformation
        document={
          vm.document && (
            <OCRScopeJobDetailBodyBasicInformationDocument
              title={vm.document.title}
              dateCreated={vm.document.dateCreated}
              onDownload={vm.downloadDocument}
            />
          )
        }
      >
        <Step
          title={'Workspace'}
          isLast={workspaceSelectorStep.position.last}
          descriptionHtml={workspaceSelectorStep.descriptionHtml}
          active={workspaceSelectorStep.isActive}
          becomeActive$={workspaceSelectorStep.becomeActive$}
          onBecomeVisible={workspaceSelectorStep.becomeVisible}
          onBecomeHidden={workspaceSelectorStep.becomeHidden}
          testId={`step-${workspaceSelectorStep.stepId}`}
          compact
        >
          <CompactWorkspaceSelectorContainer vm={workspaceSelector} />
        </Step>
        <Step
          title={'Legend'}
          isLast={legendSelectorStep.position.last}
          descriptionHtml={legendSelectorStep.descriptionHtml}
          active={legendSelectorStep.isActive}
          becomeActive$={legendSelectorStep.becomeActive$}
          onBecomeVisible={legendSelectorStep.becomeVisible}
          onBecomeHidden={legendSelectorStep.becomeHidden}
          testId={`step-${legendSelectorStep.stepId}`}
          compact
        >
          <CompactLegendSelectorContainer vm={legendSelector} isDisabled />
        </Step>
        {vm.legendTemplate && (
          <FormRow>
            <FormGroupContainer label="Template">
              <CustomSelect
                {...templateSelectProps}
                isDisabled
                placeholder="Select a template"
                value={vm.legendTemplate}
                multi={false}
                itemToString={(item) => item.name}
                onChange={vm.setLegendTemplate}
                testId={`${testId}-template-select`}
              />
            </FormGroupContainer>
          </FormRow>
        )}
        <Step
          title={'Champion client'}
          isLast={ownerSelectorStep.position.last}
          descriptionHtml={ownerSelectorStep.descriptionHtml}
          active={ownerSelectorStep.isActive}
          becomeActive$={ownerSelectorStep.becomeActive$}
          onBecomeVisible={ownerSelectorStep.becomeVisible}
          onBecomeHidden={ownerSelectorStep.becomeHidden}
          testId={`step-${ownerSelectorStep.stepId}`}
          compact
        >
          <CompactOwnerSelectorContainer vm={ownerSelector} />
        </Step>
        <Step
          title={'Team'}
          isLast={teamSelectorStep.position.last}
          descriptionHtml={teamSelectorStep.descriptionHtml}
          active={teamSelectorStep.isActive}
          becomeActive$={teamSelectorStep.becomeActive$}
          onBecomeVisible={teamSelectorStep.becomeVisible}
          onBecomeHidden={teamSelectorStep.becomeHidden}
          testId={`step-${teamSelectorStep.stepId}`}
          compact
        >
          <CompactTeamSelectorContainer vm={teamSelector} />
        </Step>
        <Step
          title={'Supporting clients'}
          isLast={supportingClientSelectorStep.position.last}
          descriptionHtml={supportingClientSelectorStep.descriptionHtml}
          active={supportingClientSelectorStep.isActive}
          becomeActive$={supportingClientSelectorStep.becomeActive$}
          onBecomeVisible={supportingClientSelectorStep.becomeVisible}
          onBecomeHidden={supportingClientSelectorStep.becomeHidden}
          testId={`step-${supportingClientSelectorStep.stepId}`}
          compact
        >
          <CompactSupportingClientSelectorContainer
            vm={supportingClientSelector}
          />
        </Step>
        {vm.editor.canEditDraftJobsAsAdmin && (
          <Step
            title={'Request provider'}
            isLast={requestProviderSelectorStep.position.last}
            descriptionHtml={requestProviderSelectorStep.descriptionHtml}
            active={requestProviderSelectorStep.isActive}
            becomeActive$={requestProviderSelectorStep.becomeActive$}
            onBecomeVisible={requestProviderSelectorStep.becomeVisible}
            onBecomeHidden={requestProviderSelectorStep.becomeHidden}
            testId={`step-${requestProviderSelectorStep.stepId}`}
            compact
          >
            <CompactRequestProviderSelectorContainer
              vm={requestProviderSelector}
            />
          </Step>
        )}
      </OCRScopeJobDetailBodyBasicInformation>
    )
  },
)
